import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Form, Select, Switch, Avatar } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import '../Users/Users.css';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  .ant-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const ContractAddresses: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [contractAddresses, setContractAddresses] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [blockchainNetworks, setBlockchainNetworks] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedBlockchainType, setSelectedBlockchainType] = useState<string | null>(null);
  const [isNativeChecked, setIsNativeChecked] = useState(false);
  // Edit functionality moved to detail page
  const [search, setSearch] = useState(searchParams.get('search') || '');
   const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [resyncingIds, setResyncingIds] = useState<Set<string>>(new Set());

  // Filter states
  const [blockchainFilter, setBlockchainFilter] = useState<string>('all');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isNativeFilter, setIsNativeFilter] = useState<string>('all');
  const [allowInMonitorFilter, setAllowInMonitorFilter] = useState<string>('all');
  const [supportsPollingFilter, setSupportsPollingFilter] = useState<string>('all');

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchContractAddresses();
    fetchCurrencies();
    fetchBlockchainNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageId, perPage]);

  const fetchContractAddresses = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (search) {
        params.search = search;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_CONTRACT_ADDRESSES,
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setContractAddresses(items);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              0,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: items.length,
          }));
        }
      }
    } catch (error: any) {
      message.error('Failed to fetch contract addresses');
      console.error('Error fetching contract addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      // Fetch currencies for the dropdown (max perPage is 1000)
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_CURRENCIES, {
        params: {
          pageId: 1,
          perPage: 1000, // Maximum allowed perPage value
        },
      });
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setCurrencies(items);
      }
    } catch (error: any) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchBlockchainNetworks = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS);
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setBlockchainNetworks(items);
      }
    } catch (error: any) {
      console.error('Error fetching blockchain networks:', error);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setIsNativeChecked(false);
    setSelectedBlockchainType(null);
    setModalVisible(true);
  };

  // Edit functionality moved to detail page

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = {
        address: values.address,
        currencyId: values.currencyId,
        blockchainNetworkId: values.blockchainNetworkId,
        isNative: values.isNative || false,
      };

      if (values.status) {
        payload.status = values.status;
      }

      // Add allowInOnchainWatcherMonitor and supportsRPCPolling
      if (values.allowInOnchainWatcherMonitor !== undefined) {
        payload.allowInOnchainWatcherMonitor = values.allowInOnchainWatcherMonitor;
      }
      if (values.supportsRPCPolling !== undefined) {
        payload.supportsRPCPolling = values.supportsRPCPolling;
      }

      // Only create, edit is done on detail page
      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_CONTRACT_ADDRESS, payload);
      message.success('Contract address created successfully');
      
      setModalVisible(false);
      fetchContractAddresses();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save contract address');
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  // Filter the contract addresses based on all filter criteria
  const filteredContractAddresses = contractAddresses.filter((contract: any) => {
    // Blockchain filter
    if (blockchainFilter !== 'all') {
      const networkId = contract.blockchainNetwork?.id || contract.blockchainNetworkId;
      if (String(networkId) !== blockchainFilter) return false;
    }

    // Currency filter
    if (currencyFilter !== 'all') {
      const currencyId = contract.currency?.id || contract.currencyId;
      if (String(currencyId) !== currencyFilter) return false;
    }

    // Status filter
    if (statusFilter !== 'all' && contract.status !== statusFilter) return false;

    // Is Native filter
    if (isNativeFilter !== 'all') {
      const isNative = contract.isNative || false;
      const address = (contract.contractAddress || '').toLowerCase();
      const isNativeToken = isNative || address === 'native';
      if (isNativeFilter === 'yes' && !isNativeToken) return false;
      if (isNativeFilter === 'no' && isNativeToken) return false;
    }

    // Allow in Monitor filter
    if (allowInMonitorFilter !== 'all') {
      const allowInMonitor = contract.allowInOnchainWatcherMonitor !== false;
      if (allowInMonitorFilter === 'yes' && !allowInMonitor) return false;
      if (allowInMonitorFilter === 'no' && allowInMonitor) return false;
    }

    // Supports RPC Polling filter
    if (supportsPollingFilter !== 'all') {
      const supportsPolling = contract.supportsRPCPolling !== false;
      if (supportsPollingFilter === 'yes' && !supportsPolling) return false;
      if (supportsPollingFilter === 'no' && supportsPolling) return false;
    }

    return true;
  });

  const hasActiveFilters = blockchainFilter !== 'all' || 
    currencyFilter !== 'all' || 
    statusFilter !== 'all' || 
    isNativeFilter !== 'all' || 
    allowInMonitorFilter !== 'all' || 
    supportsPollingFilter !== 'all' || 
    search;

  const clearAllFilters = () => {
    setBlockchainFilter('all');
    setCurrencyFilter('all');
    setStatusFilter('all');
    setIsNativeFilter('all');
    setAllowInMonitorFilter('all');
    setSupportsPollingFilter('all');
    setSearch('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.set('pageId', '1');
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (address: string, record: any) => {
        if (!address) return '-';
        const shortAddress = `${address.substring(0, 10)}...${address.substring(address.length - 8)}`;
        const explorerUrl = record.blockchainNetwork?.explorerUrl;
        if (explorerUrl) {
          // Construct explorer URL - most explorers use /address/ or /token/ pattern
          const explorerLink = `${explorerUrl.replace(/\/$/, '')}/address/${address}`;
          return (
            <a href={explorerLink} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
              {shortAddress}
            </a>
          );
        }
        return shortAddress;
      },
    },
    {
      title: 'Currency',
      key: 'currency',
      render: (_: any, record: any) => {
        // First try to get currency from populated relationship
        if (record.currency) {
          return record.currency.symbol || record.currency.name || '-';
        }
        // Fallback: look up currency from currencies state using currencyId
        if (record.currencyId) {
          const currency = currencies.find((c: any) => c.id === record.currencyId);
          if (currency) {
            return currency.symbol || currency.name || '-';
          }
        }
        return '-';
      },
    },
    {
      title: 'Blockchain Network',
      key: 'blockchainNetwork',
      render: (_: any, record: any) => {
        const network = record.blockchainNetwork;
        if (!network) return '-';
        return (
          <Space>
            {network.iconUrl && (
              <Avatar
                src={network.iconUrl}
                size="small"
                style={{ width: 20, height: 20 }}
              />
            )}
            <span>{network.name || network.abbreviation || '-'}</span>
          </Space>
        );
      },
    },
    {
      title: 'Is Native',
      dataIndex: 'isNative',
      key: 'isNative',
      render: (isNative: boolean) => isNative ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Allow in Watcher',
      dataIndex: 'allowInOnchainWatcherMonitor',
      key: 'allowInOnchainWatcherMonitor',
      render: (value: boolean) => (
        <Tag color={value !== false ? 'green' : 'red'}>
          {value !== false ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Supports RPC Polling',
      dataIndex: 'supportsRPCPolling',
      key: 'supportsRPCPolling',
      render: (value: boolean) => (
        <Tag color={value !== false ? 'green' : 'red'}>
          {value !== false ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/kivu/contract-addresses/${record.id}`)}
            size="small"
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Contract Addresses
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add Contract Address
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Search
              </Text>
              <Input
                placeholder="Search by contract address"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onBlur={() => {
                  const newParams = new URLSearchParams(searchParams);
                  if (search) {
                    newParams.set('search', search);
                  } else {
                    newParams.delete('search');
                  }
                  newParams.set('pageId', '1'); // Reset to first page on search
                  setSearchParams(newParams);
                }}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Blockchain Network
              </Text>
              <Select
                value={blockchainFilter}
                onChange={setBlockchainFilter}
                style={{ width: '100%' }}
                placeholder="All Blockchains"
              >
                <Select.Option value="all">All Blockchains</Select.Option>
                {blockchainNetworks.map((network: any) => (
                  <Select.Option key={network.id} value={network.id}>
                    {network.name} {network.abbreviation ? `(${network.abbreviation})` : ''}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Currency
              </Text>
              <Select
                value={currencyFilter}
                onChange={setCurrencyFilter}
                style={{ width: '100%' }}
                placeholder="All Currencies"
                showSearch
                optionFilterProp="children"
                filterOption={(input: string, option: any) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                <Select.Option value="all">All Currencies</Select.Option>
                {currencies.map((currency: any) => (
                  <Select.Option key={currency.id} value={currency.id}>
                    {currency.symbol} - {currency.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: '1 1 150px', minWidth: '120px' }}>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Status
              </Text>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
                placeholder="All Status"
              >
                <Select.Option value="all">All Status</Select.Option>
                <Select.Option value="ACTIVE">ACTIVE</Select.Option>
                <Select.Option value="PENDING">PENDING</Select.Option>
                <Select.Option value="SUSPENDED">SUSPENDED</Select.Option>
                <Select.Option value="DELETED">DELETED</Select.Option>
              </Select>
            </div>
            <div style={{ flex: '1 1 150px', minWidth: '120px' }}>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Is Native
              </Text>
              <Select
                value={isNativeFilter}
                onChange={setIsNativeFilter}
                style={{ width: '100%' }}
                placeholder="All"
              >
                <Select.Option value="all">All</Select.Option>
                <Select.Option value="yes">Native Only</Select.Option>
                <Select.Option value="no">Non-Native Only</Select.Option>
              </Select>
            </div>
            <div style={{ flex: '1 1 150px', minWidth: '120px' }}>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Allow in Monitor
              </Text>
              <Select
                value={allowInMonitorFilter}
                onChange={setAllowInMonitorFilter}
                style={{ width: '100%' }}
                placeholder="All"
              >
                <Select.Option value="all">All</Select.Option>
                <Select.Option value="yes">Yes</Select.Option>
                <Select.Option value="no">No</Select.Option>
              </Select>
            </div>
            <div style={{ flex: '1 1 150px', minWidth: '120px' }}>
              <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
                Supports RPC Polling
              </Text>
              <Select
                value={supportsPollingFilter}
                onChange={setSupportsPollingFilter}
                style={{ width: '100%' }}
                placeholder="All"
              >
                <Select.Option value="all">All</Select.Option>
                <Select.Option value="yes">Yes</Select.Option>
                <Select.Option value="no">No</Select.Option>
              </Select>
            </div>
            {hasActiveFilters && (
              <div style={{ flex: '0 0 auto' }}>
                <Button
                  onClick={clearAllFilters}
                  style={{ marginBottom: 8 }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ marginBottom: 12, color: COLORS.textSecondary }}>
          {hasActiveFilters ? (
            <>
              Showing {filteredContractAddresses.length} of {pagination.total} contract addresses
              {filteredContractAddresses.length !== pagination.total && (
                <span style={{ marginLeft: 8, fontSize: '0.9em', opacity: 0.8 }}>
                  (filtered)
                </span>
              )}
            </>
          ) : (
            <>Total {pagination.total} contract addresses</>
          )}
        </div>
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={filteredContractAddresses}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: hasActiveFilters ? filteredContractAddresses.length : pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  {hasActiveFilters ? (
                    <>Showing {range[0]}-{range[1]} of {total} filtered contract addresses</>
                  ) : (
                    <>Showing {range[0]}-{range[1]} of {total} contract addresses</>
                  )}
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            className="dark-table"
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="Create Contract Address"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Create"
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="currencyId"
            label="Currency"
            rules={[{ required: true, message: 'Please select currency' }]}
          >
            <Select
              placeholder="Select Currency"
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={currencies.map(currency => ({
                value: currency.id,
                label: `${currency.symbol} - ${currency.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="blockchainNetworkId"
            label="Blockchain Network"
            rules={[{ required: true, message: 'Please select blockchain network' }]}
          >
            <Select
              placeholder="Select Blockchain Network"
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                const network = blockchainNetworks.find(n => n.id === value);
                setSelectedBlockchainType(network?.blockchainType || null);
              }}
              options={blockchainNetworks.map(network => ({
                value: network.id,
                label: `${network.name} (${network.abbreviation})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="isNative"
            label="Is Native Currency"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch
              onChange={(checked) => {
                setIsNativeChecked(checked);
                if (checked) {
                  // Set address to "native" and lock it
                  form.setFieldsValue({ address: 'native' });
                } else {
                  // Clear address and unlock it
                  form.setFieldsValue({ address: '' });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="address"
            label="Contract Address"
            rules={[{ required: true, message: 'Please enter contract address' }]}
          >
            <Input 
              placeholder={isNativeChecked ? "native" : "0x..."}
              disabled={isNativeChecked}
              value={isNativeChecked ? 'native' : undefined}
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="SUSPENDED">Suspended</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="allowInOnchainWatcherMonitor"
            label="Allow in OnchainWatcher Monitor"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="supportsRPCPolling"
            label="Supports RPC Polling"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default ContractAddresses;

